import config from 'constants/config'
import { LESCAPES_SIGNUP_DOMAIN } from 'constants/auth'
import { LUXURY_ESCAPES } from 'constants/brands'
import request, { ObjectOptions } from 'api/requestUtils'
import { mapProfileResponseToUserDetails, ProfilePutResult, ProfilePutPayload } from './mappers/profileMap'
import { appViewMessageHandler } from 'lib/window/webViewUtils'
import * as Analytics from 'analytics/analytics'
import { baseContext } from 'analytics/snowplow/events'
import { reportClientError } from 'services/errorReportingService'
import { getMemberSubscription } from './luxPlus'
import { AppleLoginData, FacebookLoginData, GoogleLoginData } from 'actions/AuthActions'

interface PostAuthResponse {
  access_token: string;
  id_member: string;
  is_sign_up: boolean;
  is_new_to_brand: boolean;
}

function mapAccount(serverAccount: AccountResponseResult, luxPlusMemberSubscription: App.MemberSubscription): App.AuthAccount {
  return {
    creditsCollection: { initial: true },
    balance: 0,
    memberId: serverAccount.memberId,
    referralProgramEnabled: serverAccount.referral_program_enabled,
    givenName: serverAccount.givenName,
    surname: serverAccount.surname,
    title: serverAccount.title as App.Title ?? undefined,
    email: serverAccount.email,
    dob: serverAccount.dob,
    status: serverAccount.status,
    roles: serverAccount.roles,
    vendors: serverAccount.vendors,
    postcode: serverAccount.postcode,
    phonePrefix: serverAccount.phone_prefix,
    phone: serverAccount.phone,
    partnerships: serverAccount.partnerships ?? {},
    toggles: serverAccount.toggles ?? {},
    lastLogin: serverAccount.lastLogin ? {
      ipAddress: serverAccount.lastLogin.ip_address,
      loginDate: serverAccount.lastLogin.login_date,
    } : {},
    isSpoofed: !!serverAccount.isSpoofed,
    fullName: serverAccount.fullName,
    legacyId: serverAccount.legacy_id ?? undefined,
    legacyPlatform: serverAccount.legacy_platform ?? undefined,
    countryCode: serverAccount.country_code,
    signupDomain: serverAccount.signup_domain,
    personContactId: serverAccount.person_contact_id,
    tenant: serverAccount.tenant,
    numberOfPurchases: serverAccount.number_of_purchases,
    customerSupportCode: serverAccount.customer_support_code,
    recentlyUsedAirportCode: serverAccount.recently_used_airport_code ?? undefined,
    emailVerified: !!serverAccount.email_verified,
    shadowBanUser: serverAccount.shadow_ban_user,
    utmSource: serverAccount.utm_source,
    utmMedium: serverAccount.utm_medium,
    utmCampaign: serverAccount.utm_campaign,
    utmTerm: serverAccount.utm_term,
    utmContent: serverAccount.utm_content,
    utmAdgroup: serverAccount.utm_adgroup,
    creditsByCurrency: {},
    subscriberTier: serverAccount.subscriber_tier,
    // @ts-expect-error `add_phone_prompted_at` doesn't exist in the contract!
    addPhonePromptedAt: serverAccount.add_phone_prompted_at,
    luxPlus: {
      member: {
        /** This is used so spoofed customers can be toggled between buying as a member or as a non member */
        disableMembership: false,
        subscription: luxPlusMemberSubscription,
      },
    },
  }
}

function parseTrackAuthResponse(res: any) {
  const idMember = res?.result?.id_member
  const status = res?.status
  return { idMember, status }
}

async function authPost(
  path: string,
  data: any,
  options: ObjectOptions = {},
  trackingProvider: App.AuthSource,
  authContext: AuthPostContext = {},
) {
  const brand = authContext.brand ?? config.BRAND
  const response = await request.post<App.ApiResponse<PostAuthResponse>, any>(path, data, options)

  try {
    const { idMember, status } = parseTrackAuthResponse(response)

    if (path === '/register' || response.result?.is_sign_up) {
      Analytics.trackClientEvent({
        action: `${idMember}_${status}_succeeded`,
        subject: `${trackingProvider}_registration`,
        category: 'logging',
        type: 'operational',
      })
    } else {
      Analytics.trackClientEvent({
        action: `${idMember}_${status}_succeeded`,
        subject: `${trackingProvider}_auth`,
        category: 'logging',
        type: 'operational',
      })
    }
  } catch(error) {
    reportClientError(error)
  }

  const accessToken = response.result.access_token
  const isSignUp = !!response.result.is_sign_up || !!response.result.is_new_to_brand
  const isSignIn = !isSignUp && path !== '/register'
  const accountResponse = await me(accessToken, authContext.regionCode, authContext.luxPlusEnabled, brand)

  return {
    accessToken,
    account: accountResponse,
    isSignUp: path === '/register' || isSignUp,
    isSignIn,
  }
}

interface AuthPostContext {
  brand?: App.BrandName
  luxPlusEnabled?: boolean
  regionCode?: string
}

interface AccountResponseResult {
  givenName: string;
  title: App.Title | null;
  surname: string;
  email: string;
  fullName: string;
  dob: string;
  id_member: string;
  memberId: string;
  legacy_id: string | null;
  legacy_platform: string | null;
  status: string;
  email_verified: boolean;
  roles: Array<string>;
  vendors: Array<string>;
  country_code: string;
  postcode: string;
  phone_prefix: string;
  phone: string;
  signup_domain: string;
  customer_support_code: string;
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_content: string;
  utm_term: string;
  utm_adgroup: string;
  updated_at: string;
  qff: string;
  qff_last_name: string;
  partnerships: App.PartnershipsMap;
  gdpr: string;
  created_date: string;
  recently_used_airport_code: string | null;
  person_contact_id: string;
  flights_enabled: boolean;
  toggles: Record<string, boolean | string | null>;
  referral_program_enabled: boolean;
  lastLogin: {
    ip_address: string;
    login_date: string
  };
  tenant: string;
  number_of_purchases: number;
  vip_client: boolean;
  le_platinum: boolean | null;
  subscriber_tier: 'base' | 'free_preview' | null;
  maskedPhone: string;
  isSpoofed?: boolean;
  shadow_ban_user?: boolean;
}

export function me(accessToken?: string, regionCode?: string, luxPlusEnabled?: boolean, brand = config.BRAND) {
  const path = brand === config.BRAND ? '/me' : `/me?brand=${encodeURIComponent(brand)}`
  let options: ObjectOptions = { credentials: 'include' }
  if (accessToken) {
    options = { headers: { authorization: `Bearer ${accessToken}` } }
  }

  return request.get<App.ApiResponse<AccountResponseResult>>(
    path,
    brand === config.BRAND ? options : { ...options, excludeBrand: true },
  ).then(async response => {
    const luxPlusMemberSubscription: App.MemberSubscription = {
      item: undefined,
      error: undefined,
      fetching: false,
    }
    if (luxPlusEnabled && regionCode && response.result.memberId) {
      try {
        luxPlusMemberSubscription.item = await getMemberSubscription({
          customerId: response.result.memberId,
          regionCode,
          accessToken,
        })
      }
      catch(error) {
        luxPlusMemberSubscription.error = error
      }
    }
    return mapAccount(response.result, luxPlusMemberSubscription)
  })
}

interface CheckEmailPayloadStructure {
  brand?: string,
  email?: string,
  phone_prefix?: string;
  phone?: string;
  recaptchaResponse: string,
}

interface CheckUserResult {
  isExist: boolean;
  maskedPhone?: string;
}

export async function checkUserExists(user: { email?: string, phoneNumber?: App.PhoneNumber }, recaptchaData?: string | null) {
  return checkUserExistsForBrand(user, recaptchaData)
}

export async function checkUserExistsForBrand(
  user: { email?: string, phoneNumber?: App.PhoneNumber },
  recaptchaData?: string | null,
  brand?: App.BrandName,
) {
  return request.post<CheckUserResult, CheckEmailPayloadStructure>(
    '/api/users/exists',
    {
      email: user.email,
      // prefer email if it's provided
      phone: user.email ? undefined : user.phoneNumber?.phone,
      phone_prefix: user.email ? undefined : user.phoneNumber?.prefix,
      recaptchaResponse: recaptchaData ?? '',
      ...(brand && { brand }),
    },
    brand && brand !== config.BRAND ? { excludeBrand: true } : undefined,
  )
}

export interface AuthAccountLoginResponse {
  accessToken: string;
  account: App.AuthAccount;
  business?: App.BusinessTraveller.Business;
  employee?: App.BusinessTraveller.Employee;
}

export function login(
  user: App.User,
  password: string,
  options: LoginOptions = {},
): Promise<AuthAccountLoginResponse> {
  const brand = options.brand ?? config.BRAND
  const requestOptions: ObjectOptions = {
    ...(options.includeCredentials === false ? {} : { credentials: 'include' as const }),
    ...(brand === config.BRAND ? {} : { excludeBrand: true }),
  }

  return authPost(
    '/login/recaptcha',
    {
      login: user.email,
      phone: user.email ? undefined : user.phoneNumber?.phone,
      phone_prefix: user.email ? undefined : user.phoneNumber?.prefix,
      password,
      recaptchaResponse: options.recaptchaData,
      brand,
    },
    requestOptions,
    'email',
    {
      brand,
      luxPlusEnabled: options.luxPlusEnabled,
      regionCode: options.regionCode,
    },
  )
}

export interface LoginOptions {
  brand?: App.BrandName
  includeCredentials?: boolean
  luxPlusEnabled?: boolean
  recaptchaData?: string
  regionCode?: string
}

export function loginFacebook(data: FacebookLoginData, regionCode: string, luxPlusEnabled?: boolean) {
  return authPost(
    '/login/facebook',
    {
      ...data,
      signup_domain: LESCAPES_SIGNUP_DOMAIN,
      country_code: regionCode,
    },
    { credentials: 'include' },
    'facebook',
    { luxPlusEnabled, regionCode },
  )
}

interface SocialLoginOptions {
  brand?: App.BrandName
  includeCredentials?: boolean
}

export function loginGoogle(
  data: GoogleLoginData,
  regionCode: string,
  luxPlusEnabled?: boolean,
  options: SocialLoginOptions = {},
) {
  const brand = options.brand ?? config.BRAND

  return authPost(
    '/login/google',
    {
      ...data,
      ...(options.brand && { brand }),
      signup_domain: LESCAPES_SIGNUP_DOMAIN,
      country_code: regionCode,
    },
    {
      ...(options.includeCredentials === false ? {} : { credentials: 'include' as const }),
      ...(brand === config.BRAND ? {} : { excludeBrand: true }),
    },
    'google',
    { brand, luxPlusEnabled, regionCode },
  )
}

export function loginApple(data: AppleLoginData, regionCode: string, luxPlusEnabled?: boolean) {
  return authPost(
    '/login/apple',
    {
      ...data,
      signup_domain: LESCAPES_SIGNUP_DOMAIN,
      country_code: regionCode,
    },
    { credentials: 'include' },
    'apple',
    { luxPlusEnabled, regionCode },
  )
}

export function logout() {
  return request.post('/logout', {}, { credentials: 'include' })
}

export interface AuthAccountRegistrationResponse {
  accessToken: string;
  account: App.AuthAccount;
}

interface RegisterData extends Partial<App.AuthAccount> {
  brand?: App.BrandName;
  password: string;
  utmAdgroup?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmMedium?: string;
  utmSource?: string;
  utmTerm?: string;
}

export function register(
  data: RegisterData,
  options: RegisterOptions,
): Promise<AuthAccountRegistrationResponse> {
  const brand = options.brand ?? data.brand ?? config.BRAND
  const { email, password, surname, givenName, utmSource, utmMedium, utmCampaign, utmTerm, utmAdgroup, utmContent, phone, phonePrefix, postcode } = data
  const signUpDomain = brand === LUXURY_ESCAPES ? LESCAPES_SIGNUP_DOMAIN : null
  const payload = {
    brand,
    email,
    password,
    surname,
    givenName,
    utm_source: utmSource,
    utm_medium: utmMedium,
    utm_campaign: utmCampaign,
    utm_term: utmTerm,
    utm_adgroup: utmAdgroup,
    utm_content: utmContent,
    country_code: options.regionCode,
    ...(signUpDomain && { signup_domain: signUpDomain }),
    ...(phone && {
      phone_prefix: phonePrefix,
      phone,
    }),
    postcode,
  }
  return authPost(
    '/register',
    payload,
    {
      ...(options.includeCredentials === false ? {} : { credentials: 'include' as const }),
      ...(brand === config.BRAND ? {} : { excludeBrand: true }),
    },
    'email',
    {
      brand,
      regionCode: options.regionCode,
    },
  )
}

interface RegisterOptions {
  brand?: App.BrandName
  includeCredentials?: boolean
  regionCode: string
}

interface VerifyUserEmailParams {
  brand: string;
  token: string;
}

interface VerifyUserEmailResponse {
  status: number;
  message: string;
}

export async function requestUserEmailValidation({ brand, token }: VerifyUserEmailParams): Promise<VerifyUserEmailResponse> {
  return request.post<VerifyUserEmailResponse, Record<string, string>>('/api/auth/verify-user-email', { brand, token })
}

export function sendResetPasswordEmail(email: string, callbackPath?: string) {
  return request.post('/api/reset-password-email', { email, callbackPath }, { credentials: 'include' })
}

interface ResetPasswordParams {
  token: string;
  password: string;
  luxPlusEnabled?: boolean;
  regionCode?: string;
}

export function resetPassword({ token, password, luxPlusEnabled, regionCode }: ResetPasswordParams) {
  return request
    .put<App.ApiResponse<{ access_token: string }>, any>('/api/reset-token', { token, password }, { credentials: 'include' })
    .then(async response => ({
      accessToken: response.result.access_token,
      account: await me(response.result.access_token, regionCode, luxPlusEnabled),
    }))
}

/**
 * Builds the payload for profile updates via the svc-business proxy.
 * Includes email - the proxy merges with the linked personal account and bypasses OTP.
 */
function buildProfileUpdatePayload(
  account: Partial<App.AuthAccount>,
  supportPhoneNumber: string | null,
  employeeLoyaltyPrograms?: ProfilePutPayload['employeeLoyaltyPrograms'],
): ProfilePutPayload {
  return {
    ...(account.email != null && { email: account.email ?? '' }),
    givenName: account.givenName ?? undefined,
    surname: account.surname ?? undefined,
    title: account.title ?? undefined,
    dob: account.dob ?? undefined,
    gender: account.gender ?? undefined,
    middleName: account.middleName ?? undefined,
    country_code: account.countryCode ?? undefined,
    phone_prefix: account.phonePrefix ?? undefined,
    phone: account.phone ?? undefined,
    recently_used_airport_code: account.recentlyUsedAirportCode ?? undefined,
    postcode: account.postcode ?? undefined,
    support_phone_number: supportPhoneNumber ?? undefined,
    employeeLoyaltyPrograms: employeeLoyaltyPrograms ?? undefined,
  }
}

/**
 * Updates user profile via the svc-business proxy (PUT /api/business-traveller/me/profile).
 * The proxy updates svc-auth with service auth, bypassing OTP and merging with the linked personal account.
 */
interface UpdateUserDetailsParams {
  account: Partial<App.AuthAccount>
  supportPhoneNumber?: string | null
  employeeLoyaltyPrograms?: ProfilePutPayload['employeeLoyaltyPrograms']
}

export function updateUserDetails({
  account,
  supportPhoneNumber = null,
  employeeLoyaltyPrograms,
}: UpdateUserDetailsParams) {
  const payload = buildProfileUpdatePayload(account, supportPhoneNumber, employeeLoyaltyPrograms)
  return request.put<App.ApiResponse<ProfilePutResult>, ProfilePutPayload>(
    '/api/business-traveller/me/profile',
    payload,
    { credentials: 'include' },
  ).then(response => mapProfileResponseToUserDetails(response.result))
}

export async function updateAddPhonePromptedAt(addPhonePromptedAt: Date) {
  const response = await request.post<App.ApiResponse<string>, unknown>('/api/users/toggles',
    {
      toggle_name: 'add_phone_prompted_at',
      toggle_value: addPhonePromptedAt.toISOString(),
    }, { credentials: 'include' })
  return response.result
}

export function updatePartnershipDetails(partnershipDetails: App.PartnershipsMap) {
  return request.put('/api/partnerships/current', partnershipDetails, { credentials: 'include' })
}

export function resendEmailConfirmation() {
  return request.get('/api/resend-email-confirmation', {
    credentials: 'include',
  })
}

const DO_NATIVE_AUTHENTICATE = 'doNativeAuthenticate'
export async function webViewAccountAccess(regionCode: string, luxPlusEnabled: boolean) {
  const authPromise = new Promise<string>((resolve, reject) => {
    window.userAuthenticated = (token: string) => {
      if (token) {
        resolve(token)
      } else {
        reject('Authentication cancelled')
      }
    }

    // Must remove region because mobile client already sets it
    const analyticsContext = baseContext().filter(c => {
      return !c.schema.includes('region')
    })
    appViewMessageHandler('authenticateUser', DO_NATIVE_AUTHENTICATE, analyticsContext)
  })
  try {
    const accessToken = await authPromise

    return {
      accessToken,
      account: await me(accessToken, regionCode, luxPlusEnabled),
    }
  } catch(error) {
    if (process.env.NODE_ENV === 'development') {
      console.error(error)
    }
    // re-throw to indicate that the login failed
    throw error
  }
}

export interface ItemDetails {
  check_in?: string; // Standard hotel reservations
  start_date?: string; // Tours v1
  type?: string;
  journey?: { id: string } // Flights
  ticket?: { date?: string } // Experiences
  departure_date?: string; // Cruises
  session_id?: string; // Bedbank
  departure_id?: string; // Tours v2
}
export interface CheckRequireVerificationRequest {
  brand: string;
  regionCode: string;
  currencyCode: string;
  payments: {
    payments: Array<{ type: string }>
  };
  items: Array<ItemDetails>;
}

export interface CheckRequireVerificationResponse {
  verificationRequired: boolean;
}

export function checkRequireVerification(checkRequireVerificationRequest: CheckRequireVerificationRequest): Promise<CheckRequireVerificationResponse> {
  // Not all order creation need otp, use this to check if otp header is required for order creation.
  const { brand, regionCode, currencyCode, payments, items } = checkRequireVerificationRequest
  return request.post('/api/orders/require_verification', {
    brand,
    region_code: regionCode,
    payments,
    items,
    currency_code: currencyCode,
  }, { credentials: 'include' })
}

export function sendVerificationCode(): Promise<void> {
  // Send OTP via email
  return request.post('/api/verify/send-auth-code?without_brand=1', {}, { credentials: 'include' })
}

export interface VerifyAuthCodeResponse {
  result: { message: string, token: string }
}

export function verifyAuthCode(code: string): Promise<string> {
  // Use to check if OTP is expired or invalid
  return request.post('/api/verify/verify-auth-code?without_brand=1', { code }, { credentials: 'include' })
    .then(res => (res as VerifyAuthCodeResponse).result.token)
}

export function sendSmsCode(): Promise<void> {
  return request.post<void, any>('/api/auth/otp/send-confirmation-code', {}, { credentials: 'include' })
    .catch((error) => {
      error.code = 'send_otp_code'
      throw error
    })
}

export interface VerifySmsCodeResponse {
  message: string
  token: string
}

export function verifySmsCode(code: string): Promise<string> {
  // Use to check if OTP is expired or invalid
  return request.post('/api/auth/otp/verify-user-confirmation-code', { code }, { credentials: 'include' })
    .then(res => (res as VerifySmsCodeResponse).token)
}

export function sendOTPVerificationCode(user: App.User) {
  return request.post('/login/send-sms-otp', {
    email: user.email,
    phone: user.email ? undefined : user.phoneNumber?.phone,
    phone_prefix: user.email ? undefined : user.phoneNumber?.prefix,
    brand: config.BRAND,
  }, { credentials: 'include' })
}

export function validateOTPVerificationCode(user: App.User, code: string, regionCode?: string, luxPlusEnabled?: boolean) {
  return authPost(
    '/login/login-sms-otp',
    {
      email: user.email,
      phone: user.email ? undefined : user.phoneNumber?.phone,
      phone_prefix: user.email ? undefined : user.phoneNumber?.prefix,
      code,
      brand: config.BRAND,
    },
    { credentials: 'include' },
    'otp',
    { luxPlusEnabled, regionCode },
  )
}

interface PhoneNumberResponse {
  phone: string | null;
  prefix: string | null;
}

export function resendEmailChangeConfirmationCode(email: string, password?: string): Promise<{ status: number, message: string, error: any }> {
  return request.post<{ status: number, message: string, error: string }, { email: string, password?: string }>('/api/auth/email-change/resend-email-change-confirmation-code', { email, password }, { credentials: 'include' })
}

export function validateEmailChangeConfirmationPhoneCode(code: string, email: string): Promise<{ status: number, message: string, error: any }> {
  return request.post<{ status: number, message: string, error: string }, { code: string, email: string }>('/api/auth/email-change/validate-email-change-confirmation-phone', { code, email }, { credentials: 'include' })
}

export function checkEmailChangePossibility(): Promise<{ email_changeable: boolean, step: string, new_email: string, error?: any }> {
  return request.post<{ step: string, status: number, email_changeable: boolean, new_email: string, error?: any }, {}>('/api/auth/email-change/initialize', {}, { credentials: 'include' })
}

export function sendEmailChangeConfirmationCode(email: string, password?: string): Promise<{ status: number, message: string, error: any }> {
  return request.post<{ status: number, message: string, error: string }, { email: string, password?: string }>('/api/auth/email-change/send-email-change-confirmation-code', { email, password }, { credentials: 'include' })
}

export function validateEmailChangeConfirmationCode(code: string, email: string, password?: string, recaptchaResponse?: string): Promise<{ status?: number, message?: string, loginData?: any, result?: any, account?: any }> {
  return request.post<{ status?: number, message?: string, loginData?: any, result?: any, account?: App.User }, { code: string, email: string, password?: string, recaptchaResponse?: string }>('/api/auth/email-change/validate-email-change-confirmation', { code, email, password, recaptchaResponse }, { credentials: 'include' })
}

export function initializePhoneChange(): Promise<{ step: string, error: string, phone_number: PhoneNumberResponse }> {
  return request.post<{ step: string, error: string, phone_number: PhoneNumberResponse }, {}>('/api/auth/phone-change/initialize', {}, { credentials: 'include' })
}

export function sendPhoneChangeConfirmationCode(phoneNumber: App.PhoneNumber, password?: string, resend?: boolean): Promise<{ status: number, message: string, error: any }> {
  return request.post<{ status: number; message: string; error: string; }, { phone_number: App.PhoneNumber, password?: string, resend?: boolean }>('/api/auth/phone-change/send-confirmation-code', { phone_number: phoneNumber, password, resend }, { credentials: 'include' })
}

export function validatePhoneChangeConfirmationCode(code: string, phoneNumber: App.PhoneNumber): Promise<{ status?: number, message?: string, phone_number: App.PhoneNumber }> {
  return request.post<{ status: number, message: string, phone_number: App.PhoneNumber }, { code: string, phone_number: App.PhoneNumber }>('/api/auth/phone-change/validate-confirmation-code', { code, phone_number: phoneNumber }, { credentials: 'include' })
}
